import { Lightning, Utils } from "@lightningjs/sdk";
import { Card } from "../../base/Card";
import { FOCUS_TRANSITION } from "../../base/focus";
import { LiveIndicator } from "../../base/LiveIndicator";
import { PlaybackButton } from "./PlaybackButton";
import { formatPlaybackTime, ProgressBar } from "./ProgressBar";
import { LinearSchedule } from "./LinearSchedule";
import { PLAYER_DARK, PLAYER_PINK, playerText } from "./styles";
const SUBTITLE_SLIDE_TRANSITION = {
    duration: 0.2,
    timingFunction: "ease-out",
};
export class Controls extends Lightning.Component {
    constructor() {
        super(...arguments);
        this._focusRow = "playback";
        this._actionIndex = 0;
        this._live = false;
        this._linear = false;
        this._duration = 0;
    }
    static _template() {
        return {
            w: 1690,
            h: 269,
            Schedule: { type: LinearSchedule, visible: false },
            Tag: {
                flex: {
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 8,
                    paddingBottom: 8,
                },
                Background: {
                    w: (w) => w,
                    h: (h) => h,
                    flexItem: false,
                    rect: true,
                    color: PLAYER_PINK,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 4 },
                },
                Label: {
                    text: {
                        fontFace: "Roboto",
                        fontStyle: "700",
                        fontSize: 25,
                        precision: 1,
                        h: 29,
                        lineHeight: 29,
                        offsetY: 23.4,
                        textColor: PLAYER_DARK,
                        wordWrap: false,
                    },
                },
            },
            Title: {
                y: 53,
                text: Object.assign(Object.assign({}, playerText("", 56, 64)), { w: 1690, textOverflow: "ellipsis" }),
            },
            Playback: {
                y: 133,
                PlayPause: {
                    w: 60,
                    h: 60,
                    Icon: { x: 30, y: 30, mount: 0.5 },
                },
                Progress: {
                    x: 84,
                    type: ProgressBar,
                    signals: { seek: "seek", togglePlayback: "togglePlayback" },
                },
                Duration: {
                    x: 1585,
                    y: 9.376,
                    text: Object.assign(Object.assign({}, playerText("00:00:00", 32)), { w: 105, textAlign: "right" }),
                },
                LiveIndicator: {
                    type: LiveIndicator,
                    x: 1617,
                    y: 21,
                    size: 18,
                    visible: false,
                },
            },
            Subtitles: {
                x: 429,
                y: 209,
                w: 832,
                h: 60,
                Background: {
                    w: 832,
                    h: 60,
                    rect: true,
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: 8,
                        fillColor: 0x1a000000,
                        stroke: 1,
                        strokeColor: 0x33ffffff,
                    },
                },
                Focus: {
                    x: 70,
                    w: 254,
                    h: 60,
                    alpha: 0,
                    rect: true,
                    color: Card.BORDER_COLOR,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 8 },
                },
                IconOutline: {
                    w: 70,
                    h: 60,
                    alpha: 0,
                    texture: Lightning.Tools.getCanvasTexture((callback) => {
                        const canvas = document.createElement("canvas");
                        canvas.width = 140;
                        canvas.height = 120;
                        const context = canvas.getContext("2d");
                        context.scale(2, 2);
                        const gradient = context.createLinearGradient(0, 0, 70, 0);
                        gradient.addColorStop(0, "white");
                        gradient.addColorStop(0.5, "rgba(255,255,255,0)");
                        gradient.addColorStop(1, "white");
                        context.strokeStyle = gradient;
                        context.lineWidth = 1;
                        context.beginPath();
                        context.moveTo(8, 0.5);
                        context.arcTo(69.5, 0.5, 69.5, 59.5, 7.5);
                        context.arcTo(69.5, 59.5, 0.5, 59.5, 7.5);
                        context.arcTo(0.5, 59.5, 0.5, 0.5, 7.5);
                        context.arcTo(0.5, 0.5, 69.5, 0.5, 7.5);
                        context.closePath();
                        context.stroke();
                        callback(null, canvas);
                    }, "player-subtitle-outline"),
                },
                Icon: {
                    x: 17.5,
                    y: 12.5,
                    w: 35,
                    h: 35,
                    texture: Lightning.Tools.getSvgTexture(Utils.asset("images/player/subtitles.svg"), 70, 70),
                },
                Languages: {
                    x: 70,
                    children: ["FRENCH", "ENGLISH", "SPANISH"].map((label, index) => ({
                        type: PlaybackButton,
                        x: index * 254,
                        w: 254,
                        label,
                        sharedFocus: true,
                    })),
                },
            },
            Multiview: {
                x: 1165,
                y: 76,
                w: 200,
                h: 60,
                visible: false,
                Background: {
                    w: 200,
                    h: 60,
                    rect: true,
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: 8,
                        fillColor: 0x1a000000,
                        stroke: 1,
                        strokeColor: 0x33ffffff,
                    },
                },
                Button: {
                    type: PlaybackButton,
                    w: 200,
                    label: "MULTIVIEW",
                    iconSrc: Utils.asset("images/player/multiview.svg"),
                },
            },
            StatsPrompt: {
                x: 696,
                y: 160,
                w: 298,
                h: 31,
                visible: false,
                Prompt: {
                    text: playerText("PRESS", 30, 31),
                },
                Icon: {
                    x: 76,
                    w: 31,
                    h: 31,
                    texture: Lightning.Tools.getSvgTexture(Utils.asset("images/player/game-stats-down.svg"), 62, 62),
                },
                Label: {
                    x: 115,
                    text: playerText("FOR MATCH STATS", 30, 31),
                },
            },
        };
    }
    set title(title) {
        this.tag("Title").patch({ text: { text: title } });
    }
    set category(category) {
        this.tag("Tag.Label").patch({ text: { text: category } });
    }
    set paused(paused) {
        this.tag("Playback.PlayPause")
            .tag("Icon")
            .patch({
            w: paused ? 32 : 32.309,
            h: 41.12,
            texture: Lightning.Tools.getSvgTexture(Utils.asset(paused ? "images/play.svg" : "images/player/pause.svg"), paused ? 64 : 65, 83),
        });
    }
    set currentTime(currentTime) {
        this.tag("Playback.Progress").currentTime = currentTime;
    }
    set live(live) {
        this._live = live;
        this.patch({
            h: live ? 191 : 269,
            Tag: { visible: !live },
            Title: { visible: !live },
            Playback: {
                y: live ? 0 : 133,
                Progress: { barWidth: live ? 1509 : ProgressBar.W, live },
                Duration: {
                    x: live ? 1643 : 1585,
                    text: {
                        w: live ? 47 : 105,
                        text: live ? "LIVE" : formatPlaybackTime(this._duration),
                    },
                },
                LiveIndicator: { visible: live },
            },
            Subtitles: {
                x: live ? 325 : 429,
                y: live ? 76 : 209,
            },
            Multiview: { visible: live },
            StatsPrompt: { visible: live, y: 160 },
        });
    }
    set linear(linear) {
        this._linear = linear;
        this.tag("Schedule").visible = linear;
        if (linear)
            this.patch({
                h: 355,
                Playback: { y: 164 },
                Subtitles: { x: 429, y: 240 },
                Multiview: { visible: false },
                StatsPrompt: { y: 324 },
            });
    }
    set programme(programme) {
        this.tag("Schedule").programme = programme;
    }
    set duration(duration) {
        this._duration = duration;
        this.tag("Playback.Progress").duration = duration;
        this.tag("Playback.Duration").patch({
            text: { text: this._live ? "LIVE" : formatPlaybackTime(duration) },
        });
    }
    reset() {
        this._focusRow = "playback";
        this._actionIndex = 0;
        this.linear = false;
        this.live = false;
        this.currentTime = 0;
        this.duration = 0;
        this.paused = true;
        this._refocus();
    }
    togglePlayback() {
        this.signal("togglePlayback");
    }
    seek(delta) {
        if (!this._live)
            this.signal("seek", delta);
    }
    _getFocused() {
        if (this._focusRow === "actions") {
            if (this._actionIndex === 3)
                return this.tag("Multiview.Button");
            return this.tag("Subtitles.Languages").children[this._actionIndex];
        }
        return this.tag("Playback.Progress");
    }
    _focus() {
        this.updateSubtitleFocus(false);
    }
    _focusChange() {
        this.updateSubtitleFocus();
    }
    _unfocus() {
        this.tag("Subtitles.Focus").setSmooth("alpha", 0, FOCUS_TRANSITION);
        this.tag("Subtitles.IconOutline").setSmooth("alpha", 0, FOCUS_TRANSITION);
    }
    updateSubtitleFocus(animate = true) {
        var _a;
        const focus = this.tag("Subtitles.Focus");
        this.tag("Subtitles.IconOutline").setSmooth("alpha", this._focusRow === "actions" && this._actionIndex < 3 ? 1 : 0, FOCUS_TRANSITION);
        if (this._focusRow !== "actions" || this._actionIndex === 3) {
            focus.setSmooth("alpha", 0, FOCUS_TRANSITION);
            return;
        }
        const x = this.tag("Subtitles.Languages").x + this._getFocused().x;
        if (animate && focus.alpha > 0) {
            focus.setSmooth("x", x, SUBTITLE_SLIDE_TRANSITION);
        }
        else {
            (_a = focus.transition("x")) === null || _a === void 0 ? void 0 : _a.stop();
            focus.x = x;
        }
        focus.setSmooth("alpha", 1, FOCUS_TRANSITION);
    }
    _handleLeft() {
        if (this._focusRow === "actions")
            this._actionIndex = Math.max(0, this._actionIndex - 1);
        this._refocus();
        return true;
    }
    _handleRight() {
        if (this._focusRow === "actions")
            this._actionIndex = Math.min(this._live && !this._linear ? 3 : 2, this._actionIndex + 1);
        this._refocus();
        return true;
    }
    _handleUp() {
        this._focusRow = "playback";
        this._refocus();
        return true;
    }
    _handleDown() {
        if (this._focusRow === "playback")
            this._focusRow = "actions";
        else if (this._live)
            this.signal("showStats");
        this._refocus();
        return true;
    }
}
export default Controls;
//# sourceMappingURL=Controls.js.map